// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
/**
 * What review sends when an operator corrects, confirms or regroups a document.
 *
 * Built in one place so that every road in records the same events and teaches the
 * same lessons (docs/architecture/learning-north-star.md): the review screen calls
 * these, and so does support acting for an operator at a computer nobody is sitting
 * at (main/support-review.ts). A correction is ground truth whichever road it came
 * by; a second copy of these rules would drift from this one, and the drift would be
 * in the training set.
 *
 * Pure: no React, no window, no engine. Each builder returns what to save; the
 * caller saves it through the channel the window uses (desktop-api.ts).
 *
 * `attribution` is added to every correction's metadata (`{ "via": "support" }`)
 * so the job's history says which road a correction came by. The window passes none,
 * and what it sends is then exactly what it sent before this module existed.
 */

import {
  CORRECTION,
  TYPE_FROM_OPERATOR_SPLIT,
  UNCLASSIFIED_DOCUMENT,
  type BarcodeResult,
  type CorrectionRequest,
  type DocumentCorrectionBatchEntry,
  type DocumentResult,
  type FieldInterpretation,
  type FieldResult,
} from '../shared/desktop-api';
import { fieldCorrectionIsCorrection, valueIsHumanAuthored } from '../shared/field-correction-kind';
import { mergeDocumentSources, partitionDocumentSources } from './boundary-source-provenance';
import { retainedFieldEvidence, uniqueTypedFieldEvidence } from './field-correction';
import { agreeingUncheckedRows } from './review-main-action';

export type ReviewCorrection = CorrectionRequest['correction'];
/** Added to each correction's metadata; null for the window's own corrections. */
export type ReviewAttribution = Readonly<Record<string, string>> | null | undefined;
/** A configured field as review reads it. */
export type ReviewField = {
  readonly name: string;
  readonly kind: string;
  readonly required: boolean;
};
/** A job's document type as review reads it. */
export type ReviewType = {
  readonly id: string;
  readonly name: string;
  readonly schemaVersion: number;
};

/** The metadata the window wrote, with the attribution beside it. */
export const attributed = (
  correction: ReviewCorrection,
  attribution: ReviewAttribution,
): ReviewCorrection => {
  if (!attribution) return correction;
  const metadata = correction.metadataJson
    ? (JSON.parse(correction.metadataJson) as Record<string, unknown>)
    : {};
  return { ...correction, metadataJson: JSON.stringify({ ...metadata, ...attribution }) };
};

/** One field of one document, whether by its configured id or its name. */
export const sameFieldKey = (left: FieldResult, right: FieldResult) =>
  (left.fieldId ?? left.name).localeCompare(right.fieldId ?? right.name, undefined, {
    sensitivity: 'accent',
  }) === 0;

// ---------------------------------------------------------------- a value

/**
 * A value the operator typed, chose on the page, or (empty) left intentionally blank:
 * the field as it is saved, and its correction. `before` is the field as it stood when
 * the edit began (the ledger's "before"); `existing` is the field the save replaces.
 * `viewedPage` is the page the operator was looking at, when there was one.
 */
export const fieldValueEdit = (input: {
  readonly document: DocumentResult;
  readonly configured: ReviewField;
  readonly value: string;
  readonly interpretation: FieldInterpretation;
  readonly before: FieldResult | undefined;
  readonly existing: FieldResult | undefined;
  readonly evidenceLineIndexes: readonly number[];
  readonly barcodeEvidence: BarcodeResult | null | undefined;
  readonly viewedPage: number | undefined;
  readonly attribution?: ReviewAttribution;
}): {
  field: FieldResult;
  document: DocumentResult;
  correction: ReviewCorrection;
  isCorrection: boolean;
} => {
  const {
    document,
    configured,
    value,
    interpretation,
    before,
    existing,
    barcodeEvidence,
    viewedPage,
  } = input;
  const evidenceLineIndexes = [...input.evidenceLineIndexes];
  const isCorrection = fieldCorrectionIsCorrection(before, value);
  const keptEvidence = retainedFieldEvidence(before, value);
  const locatedTypedEvidence =
    evidenceLineIndexes.length === 0 && !barcodeEvidence && isCorrection
      ? uniqueTypedFieldEvidence(document.pages, value)
      : null;
  const savedEvidenceLineIndexes =
    evidenceLineIndexes.length > 0
      ? evidenceLineIndexes
      : (locatedTypedEvidence?.evidenceLineIndexes ?? []);
  const savedEvidencePageNumber =
    evidenceLineIndexes.length > 0 || barcodeEvidence
      ? (viewedPage ?? document.pages[0]?.number ?? 1)
      : (locatedTypedEvidence?.evidencePageNumber ?? viewedPage ?? document.pages[0]?.number ?? 1);
  const field: FieldResult = {
    ...(existing ?? {
      name: configured.name,
      confidence: 1,
      source: 'operator',
      needsReview: false,
      evidencePageNumber: viewedPage ?? 1,
      evidenceLineIndexes: [],
    }),
    value,
    confidence: 1,
    // The operator's source only on a real correction. A value assigned or typed that equals what the
    // reader already had stays the reader's (its lines may still move to the selection): stamped the
    // operator's, the next identical assign was filed as a correction of the reader, which it is not.
    source:
      !isCorrection && before
        ? before.source
        : barcodeEvidence
          ? 'operator barcode selection'
          : evidenceLineIndexes.length
            ? 'operator OCR selection'
            : 'operator',
    needsReview: configured.required && !value.trim(),
    evidencePageNumber: savedEvidencePageNumber,
    evidenceLineIndexes: savedEvidenceLineIndexes,
    canonicalValue: interpretation.canonicalValue ?? null,
    validationStatus: interpretation.status,
    validationMessage: interpretation.message ?? null,
    verified: true,
    // Against the value as it stood before this edit began, not against the draft
    // the box is already holding: typing is what replaces a value, and by the time
    // a blur reaches here the draft and the typed value are the same string.
    ...keptEvidence,
    barcodeEvidence:
      barcodeEvidence ?? (evidenceLineIndexes.length ? null : keptEvidence.barcodeEvidence),
  };
  const fields = document.fields.some((item) => item.name === configured.name)
    ? document.fields.map((item) => (item.name === configured.name ? field : item))
    : [...document.fields, field];
  return {
    field,
    document: { ...document, fields },
    isCorrection,
    correction: attributed(
      {
        // Not `original?.value === value`. That made an operator's own value read
        // as a reader hit the moment it was re-saved unchanged — a second blur, or
        // the detached fields panel sending the same edit — and because
        // ApplyFieldValueVerification UPDATEs the observation, the re-save
        // DOWNGRADED an existing correction to a verification. An idempotent save
        // quietly removing a correction from the training set.
        kind: isCorrection ? CORRECTION.fieldValueCorrected : CORRECTION.fieldValueVerified,
        targetId: `${document.id}:${configured.name}`,
        beforeJson: JSON.stringify({
          documentId: document.id,
          fieldName: configured.name,
          field: before ?? null,
        }),
        afterJson: JSON.stringify({ documentId: document.id, fieldName: configured.name, field }),
        metadataJson: JSON.stringify({
          fieldKind: configured.kind,
          evidencePageNumber: field.evidencePageNumber,
          evidenceLineIndexes: savedEvidenceLineIndexes,
        }),
      },
      input.attribution,
    ),
  };
};

// ---------------------------------------------------------------- the whole document

/** The configured values a confirmation vouches for: read, and not confirmed yet. */
export const unconfirmedFields = <F extends ReviewField>(
  document: DocumentResult,
  configuredFields: readonly F[],
): F[] =>
  configuredFields.filter(
    (configured) =>
      configured.kind !== 'line-items' &&
      Boolean(document.fields.find((field) => field.name === configured.name)?.value?.trim()) &&
      !document.fields.find((field) => field.name === configured.name)?.verified,
  );

/** Confirming would change nothing: every value, row and the type confirmed, and the whole sealed. */
export const confirmationChangesNothing = (
  document: DocumentResult,
  unconfirmed: readonly ReviewField[],
): boolean =>
  unconfirmed.length === 0 &&
  document.lineItems.every((row) => row.verified) &&
  Boolean(document.documentTypeVerified) &&
  Boolean(document.confirmedAsWhole);

/**
 * Confirm on a stored document: every value and agreeing row it vouches for, where it
 * starts, its type, then the whole-document receipt, as one ordered list the engine
 * saves at once (save-document-corrections). Throws the sentence the window shows when
 * the document cannot be confirmed as it is.
 *
 * `filedTypeId` is the job's type the document counts as (document-type-identity.ts);
 * `settle` is the window's optimistic settling (the engine settles again on its way to disk).
 */
export const confirmationEntries = (input: {
  readonly target: DocumentResult;
  readonly unconfirmed: readonly ReviewField[];
  readonly configuredFields: readonly ReviewField[];
  readonly documentSchema: ReviewType | undefined;
  readonly filedTypeId: (document: DocumentResult) => string | undefined;
  readonly settle: (document: DocumentResult) => DocumentResult;
  readonly attribution?: ReviewAttribution;
}): { entries: DocumentCorrectionBatchEntry[]; document: DocumentResult } => {
  const { unconfirmed, configuredFields, documentSchema, settle } = input;
  const correction = (value: ReviewCorrection) => attributed(value, input.attribution);
  let current = input.target;
  const entries: DocumentCorrectionBatchEntry[] = [];
  for (const configured of unconfirmed) {
    const existing = current.fields.find((field) => field.name === configured.name);
    if (!existing) continue;
    const verifiedField: FieldResult = { ...existing, verified: true, needsReview: false };
    current = settle({
      ...current,
      fields: current.fields.map((field) =>
        field.name === configured.name ? verifiedField : field,
      ),
    });
    entries.push({
      document: current,
      corrections: [
        correction({
          // Asked, not assumed: a value carrying an operator source is the
          // operator's, whatever button confirmed it (field-correction-kind.ts).
          kind: valueIsHumanAuthored(existing)
            ? CORRECTION.fieldValueCorrected
            : CORRECTION.fieldValueVerified,
          targetId: `${current.id}:${configured.name}`,
          beforeJson: JSON.stringify({
            documentId: current.id,
            fieldName: configured.name,
            field: existing,
          }),
          afterJson: JSON.stringify({
            documentId: current.id,
            fieldName: configured.name,
            field: verifiedField,
          }),
          metadataJson: JSON.stringify({
            fieldKind: configured.kind,
            evidencePageNumber: verifiedField.evidencePageNumber,
            evidenceLineIndexes: verifiedField.evidenceLineIndexes ?? [],
          }),
        }),
      ],
    });
  }

  // Lines whose numbers agree but carry no check: the answer Confirm table used to give,
  // for these lines only (agreeingUncheckedRows). A line that doesn't add up keeps its question.
  for (const before of agreeingUncheckedRows(current.lineItems)) {
    const after = { ...before, verified: true, arithmeticValidated: true };
    current = settle({
      ...current,
      lineItems: current.lineItems.map((row) => (row.rowNumber === before.rowNumber ? after : row)),
    });
    entries.push({
      document: current,
      corrections: [
        correction({
          kind: CORRECTION.lineItemCorrected,
          targetId: `${current.id}:${before.rowNumber}`,
          beforeJson: JSON.stringify({ documentId: current.id, row: before }),
          afterJson: JSON.stringify({ documentId: current.id, row: after }),
        }),
      ],
    });
  }

  // Where this document begins is part of what is vouched for. A page-one opening
  // teaches nothing, but every later page inside the document teaches a continuation.
  const opensAt = current.pages[0]?.number;
  if (opensAt !== undefined && (opensAt > 1 || current.pages.length > 1)) {
    const opening = current.pages[0];
    const verifiedDocumentBatchPages = current.pages.map((page) => page.number);
    current =
      opensAt > 1
        ? {
            ...current,
            pages: current.pages.map((page, index) =>
              index !== 0
                ? page
                : {
                    ...page,
                    boundaryConfidence: 1,
                    boundaryReasons: ['You confirmed this document starts here'],
                  },
            ),
          }
        : current;
    entries.push({
      document: current,
      corrections: [
        correction({
          kind: CORRECTION.boundaryVerified,
          targetId: `${current.id}:boundaries`,
          beforeJson: JSON.stringify({
            documentId: current.id,
            batchPages: verifiedDocumentBatchPages,
            boundaryConfidence: opening?.boundaryConfidence,
            boundaryReasons: opening?.boundaryReasons ?? [],
          }),
          afterJson: JSON.stringify({
            documentId: current.id,
            batchPages: verifiedDocumentBatchPages,
          }),
          metadataJson: JSON.stringify({
            verifiedStartBatchPage: opensAt,
            verifiedDocumentBatchPages,
          }),
        }),
      ],
    });
  }

  if (!current.documentTypeVerified && documentSchema) {
    // By identity: a document read before a rename is its type under the current name,
    // and confirming it says so; it is not a change of type.
    if (input.filedTypeId(current) !== documentSchema.id)
      throw new Error('Choose the correct document type before confirming this document.');
    const beforeType = current;
    current = {
      ...current,
      documentType: documentSchema.name,
      documentTypeId: documentSchema.id,
      documentTypeSchemaVersion: documentSchema.schemaVersion,
      documentTypeConfidence: 1,
      documentTypeVerified: true,
      fields: current.fields.map((field) =>
        field.name === 'Document type'
          ? {
              ...field,
              value: documentSchema.name,
              confidence: 1,
              source: 'operator selected type',
              needsReview: false,
              verified: true,
            }
          : field,
      ),
    };
    current = settle(current);
    entries.push({
      document: current,
      corrections: [
        correction({
          kind: CORRECTION.documentTypeVerified,
          targetId: current.id,
          beforeJson: JSON.stringify({
            documentType: beforeType.documentType,
            documentTypeConfidence: beforeType.documentTypeConfidence,
            documentTypeVerified: beforeType.documentTypeVerified ?? false,
            fields: beforeType.fields,
          }),
          afterJson: JSON.stringify({
            documentType: current.documentType,
            documentTypeConfidence: current.documentTypeConfidence,
            documentTypeVerified: true,
            fields: current.fields,
          }),
        }),
      ],
    });
  }

  const refusal = confirmationRefusalOf(current, configuredFields);
  if (refusal) throw new Error(refusal);

  entries.push({
    document: current,
    corrections: [
      correction({
        kind: CORRECTION.documentConfirmed,
        targetId: current.id,
        beforeJson: JSON.stringify({ documentId: current.id }),
        afterJson: JSON.stringify({ documentId: current.id }),
        metadataJson: JSON.stringify({ confirmedWithDocument: true }),
      }),
    ],
  });
  return { entries, document: current };
};

/** Why a document cannot be confirmed as it stands, in the window's words, or null. */
export const confirmationRefusalOf = (
  current: DocumentResult,
  configuredFields: readonly ReviewField[],
): string | null => {
  const missingRequired = configuredFields.some(
    (configured) =>
      configured.required &&
      (configured.kind === 'line-items'
        ? current.lineItems.length === 0
        : !current.fields.some(
            (field) => field.name === configured.name && Boolean(field.value.trim()),
          )),
  );
  if (missingRequired) return 'Fill every required value before confirming the document.';
  if (
    current.lineItems.some(
      (row) =>
        row.quantity != null &&
        row.unitPrice != null &&
        row.amount != null &&
        !row.arithmeticValidated,
    )
  )
    return 'Resolve the line-item arithmetic before confirming the document.';
  if (
    current.fields.some(
      (field) =>
        field.name !== 'Document type' &&
        Boolean((field.canonicalValue ?? field.value).trim()) &&
        !field.verified,
    )
  )
    return 'Confirm every captured value before confirming the document.';
  if (current.notReadReason || current.pages.some((page) => page.notReadReason))
    return 'A page that was not read cannot be confirmed for export.';
  return null;
};

// ---------------------------------------------------------------- the type

/**
 * A different type chosen for a document: the operator's answer saved first, with its
 * fields still to be read (so an interrupted read never leaves empty fields that look
 * like answers). The fields are then read again for the type (reextract-prepared-document)
 * and saved with `typeFieldsCorrection`.
 */
export const typeCorrection = (
  previous: DocumentResult,
  schema: ReviewType,
  attribution?: ReviewAttribution,
) => ({
  document: {
    ...previous,
    documentType: schema.name,
    documentTypeId: schema.id,
    documentTypeSchemaVersion: schema.schemaVersion,
    documentTypeConfidence: 1,
    documentTypeVerified: true,
    confirmedAsWhole: false,
    fields: [],
    lineItems: [],
    validationFindings: [],
    needsReview: true,
    notReadReason: 'The document type was saved. Its fields still need to be read.',
  } satisfies DocumentResult,
  correction: attributed(
    {
      kind: CORRECTION.documentTypeCorrected,
      targetId: previous.id,
      beforeJson: JSON.stringify(previous),
      afterJson: JSON.stringify({ documentType: schema.name, documentTypeVerified: true }),
    },
    attribution,
  ),
});

/** The fields read for a type: after a correction (`document-type-fields-refreshed`), or a type confirmed as it was read. */
export const typeFieldsCorrection = (
  previous: DocumentResult,
  next: DocumentResult,
  isCorrection: boolean,
  attribution?: ReviewAttribution,
): ReviewCorrection =>
  attributed(
    {
      kind: isCorrection ? 'document-type-fields-refreshed' : CORRECTION.documentTypeVerified,
      targetId: previous.id,
      beforeJson: JSON.stringify({
        documentType: previous.documentType,
        documentTypeConfidence: previous.documentTypeConfidence,
        documentTypeVerified: previous.documentTypeVerified ?? false,
        fields: previous.fields,
      }),
      afterJson: JSON.stringify({
        documentType: next.documentType,
        documentTypeConfidence: next.documentTypeConfidence,
        documentTypeVerified: true,
        fields: next.fields,
      }),
    },
    attribution,
  );

// ---------------------------------------------------------------- where documents start

/** The documents a boundary change is built from. Only the changed ones need be whole; of the others, only ids and page numbers are read. */
export type BoundaryWorking = { readonly documents: readonly DocumentResult[] };
export type BoundaryChangeOf<W extends BoundaryWorking, S> = {
  kind: typeof CORRECTION.boundarySplit | typeof CORRECTION.boundaryMerge;
  mutation: {
    position: number;
    removedDocumentIds: readonly string[];
    documents: readonly DocumentResult[];
  };
  next: W;
  targetId: string;
  metadata: object;
  affectedDocumentIds: string[];
  /** The stored documents this change was built from: the engine checks they are still current. */
  expected: DocumentResult[];
  // Corrections one side of the boundary had that the surviving document could
  // not keep. Empty for every path but a merge of two documents the operator
  // had corrected the same field on.
  superseded: {
    documentId: string;
    fieldName: string;
    kept: FieldResult;
    discarded: FieldResult;
    discardedFromDocumentId: string;
  }[];
  // When both sides already have the same configured type, the merge has not
  // made classification ambiguous. Re-reading it against every type can exceed
  // the model's one-pass schema budget and, worse, asks a question whose answer
  // is already known. Splits and genuinely mixed-type merges still classify
  // against the whole job.
  recheckDocumentTypes?: S[];
  /** Where an operator still looking at the changed document lands: the new document, or the joined pages. */
  landOn: number;
  /** A split leaves a document with no type: ask for one where the operator is looking. */
  newDocumentId?: string;
};

const newDocumentIdentity = () => crypto.randomUUID().replaceAll('-', '');

/** A new document starts at `pageIndex` of document `documentIndex`, or the sentence saying why it cannot. */
export const buildSplit = <W extends BoundaryWorking, S = never>(
  working: W,
  documentIndex: number,
  pageIndex: number,
  newId: () => string = newDocumentIdentity,
): BoundaryChangeOf<W, S> | string => {
  const original = working.documents[documentIndex];
  // Final review B4 (ADR-0091 §5): Split on a kept-whole file's suggestion answers it. With no likely
  // start left in the first piece, its opening is the person's, as the store saves it (DocumentStore,
  // boundary split): not still held at the lowered confidence the suggestion gave it.
  const suggested =
    original.pages.slice(1).some((page) => page.detectedStart === true) &&
    !(original.pages[0]?.boundaryReasons ?? []).some(
      (reason) => /you confirmed/i.test(reason) || reason.startsWith('Kept whole:'),
    );
  const settled =
    suggested && !original.pages.slice(1, pageIndex).some((page) => page.detectedStart === true);
  const firstPages = original.pages.slice(0, pageIndex).map((page, index) =>
    index === 0 && settled
      ? {
          ...page,
          boundaryConfidence: 1,
          boundaryReasons: ['You confirmed this document starts here'],
        }
      : page,
  );
  const secondPages = original.pages.slice(pageIndex).map((page, index) =>
    index === 0
      ? {
          ...page,
          startsNewDocument: true,
          boundaryConfidence: 1,
          boundaryReasons: ['Operator inserted boundary'],
        }
      : page,
  );
  const sourcePartition = partitionDocumentSources(original, pageIndex);
  if (!sourcePartition)
    return 'This boundary cannot be saved safely because the pages are missing their source-file history. Read this batch again, then split it.';
  const firstPageNumbers = new Set(firstPages.map((page) => page.number));
  const secondPageNumbers = new Set(secondPages.map((page) => page.number));
  // The second half already keeps only verified fields whose evidence is on its
  // own pages. The first half kept every field wholesale, so a split left both
  // documents asserting the same operator-confirmed value while only one of them
  // holds the page it was read from. The engine's own page filter cannot correct
  // this: PreserveReviewedState puts a verified field straight back without
  // testing its page. Un-verify rather than drop, so a job with no configured
  // types — where reclassification never runs — is left with the value to
  // re-confirm rather than a hole.
  const first = {
    ...original,
    ...sourcePartition.first,
    pages: firstPages,
    fields: original.fields.map((field) =>
      field.verified &&
      field.evidencePageNumber != null &&
      !firstPageNumbers.has(field.evidencePageNumber)
        ? { ...field, verified: false }
        : field,
    ),
    lineItems: original.lineItems.filter((row) => firstPageNumbers.has(row.pageNumber)),
    needsReview: true,
  };
  const retainedSecondFields = original.fields.filter(
    (field) =>
      field.verified &&
      field.evidencePageNumber != null &&
      secondPageNumbers.has(field.evidencePageNumber),
  );
  const second = {
    ...original,
    ...sourcePartition.second,
    id: newId(),
    documentType: UNCLASSIFIED_DOCUMENT,
    documentTypeId: null,
    documentTypeSchemaVersion: 0,
    documentTypeConfidence: 0,
    documentTypeVerified: false,
    documentTypeSource: TYPE_FROM_OPERATOR_SPLIT,
    documentTypeOriginalPrediction:
      original.documentTypeOriginalPrediction ?? original.documentType,
    documentTypeOriginalConfidence:
      original.documentTypeOriginalConfidence ?? original.documentTypeConfidence,
    documentTypeExampleSupport: 0,
    documentTypeMatchingCues: undefined,
    pages: secondPages,
    fields: [
      {
        name: 'Document type',
        value: UNCLASSIFIED_DOCUMENT,
        confidence: 1,
        source: TYPE_FROM_OPERATOR_SPLIT,
        needsReview: true,
        evidencePageNumber: secondPages[0].number,
        evidenceLineIndexes: [],
      },
      ...retainedSecondFields,
    ],
    lineItems: original.lineItems
      .filter((row) => secondPageNumbers.has(row.pageNumber))
      .map((row, index) => ({ ...row, rowNumber: index + 1 })),
    validationFindings: [],
    needsReview: true,
  };
  return {
    kind: CORRECTION.boundarySplit,
    mutation: {
      position: documentIndex,
      removedDocumentIds: [original.id],
      documents: [first, second],
    },
    next: {
      ...working,
      documents: [
        ...working.documents.slice(0, documentIndex),
        first,
        second,
        ...working.documents.slice(documentIndex + 1),
      ],
    },
    targetId: original.id,
    metadata: { splitBeforeBatchPage: secondPages[0].number },
    affectedDocumentIds: [first.id, second.id],
    expected: [original],
    superseded: [],
    landOn: secondPages[0].number,
    newDocumentId: second.id,
  };
};

/**
 * Document `documentIndex` joins the one before it, or the sentence saying why it cannot.
 * `schemas` is the job's types as a capture is read with them (schemaRequest).
 */
export const buildMerge = <
  W extends BoundaryWorking,
  S extends { readonly id?: string; readonly name: string },
>(
  working: W,
  documentIndex: number,
  schemas: readonly S[],
): BoundaryChangeOf<W, S> | string => {
  const previous = working.documents[documentIndex - 1];
  const current = working.documents[documentIndex];
  const sameType = previous.documentType === current.documentType;
  const mergedSource = mergeDocumentSources([previous, current], previous.sourceName);
  if (!mergedSource)
    return 'These pages are missing their source-file history, so they were not merged';
  // Derive ownership from the pages, not the document-level union. Batches
  // split by pre-provenance builds copied A|B|C onto both halves; unioning
  // those contaminated headers when B and C were merged silently put A back.
  // Preserve the page identity exactly as it was stored. Pre-provenance
  // batches legitimately have no page.sourcePath; replacing that null with a
  // derived path here makes the engine's immutable-page check reject an
  // otherwise safe legacy merge. The engine owns the same source-####
  // fallback and derives the document header from those preserved pages.
  const previousPages = previous.pages.map((page) => ({ ...page }));
  const currentPages = current.pages.map((page, index) => ({
    ...page,
    ...(index === 0
      ? { startsNewDocument: false, boundaryConfidence: 0, boundaryReasons: [] }
      : {}),
  }));
  const mergedPages = [...previousPages, ...currentPages];
  const mergedProtectedFields = [...previous.fields, ...current.fields]
    .filter((field) => field.verified && !field.name.toLocaleLowerCase().includes('document type'))
    .filter(
      (field, index, fields) =>
        fields.findIndex((candidate) => sameFieldKey(candidate, field)) === index,
    );
  // One value has to win a merge, and the one on the earlier document does. The
  // defect was never which one wins — it was that the other one left without a
  // word: the dedupe above is first-index-wins, so an operator who had corrected
  // the same field on both documents lost the second correction with no event
  // and nothing on screen. Name what was replaced, leave that on the merged
  // document until it is dismissed, and write it to the correction ledger the
  // way every other value change is written.
  const supersededValues = current.fields
    .filter((field) => field.verified && !field.name.toLocaleLowerCase().includes('document type'))
    .flatMap((discarded) => {
      const kept = previous.fields.find(
        (candidate) => candidate.verified && sameFieldKey(candidate, discarded),
      );
      return kept && (kept.value ?? '').trim() !== (discarded.value ?? '').trim()
        ? [{ fieldName: kept.name, kept, discarded, discardedFromDocumentId: current.id }]
        : [];
    });
  const merged = {
    ...previous,
    ...mergedSource.identity,
    pages: mergedPages,
    documentType: sameType ? previous.documentType : UNCLASSIFIED_DOCUMENT,
    documentTypeId: sameType ? previous.documentTypeId : null,
    documentTypeSchemaVersion: sameType ? previous.documentTypeSchemaVersion : 0,
    documentTypeConfidence: sameType ? previous.documentTypeConfidence : 0,
    // Unverified rows only where a confirmed value has not already taken the
    // field. Without that test a merge produced two rows for one field — the
    // earlier document's empty one and the later document's confirmed one — and
    // which of them anything read depended on whether it searched or indexed.
    fields: sameType
      ? [
          ...previous.fields.filter(
            (field) =>
              !field.verified && !mergedProtectedFields.some((kept) => sameFieldKey(kept, field)),
          ),
          ...mergedProtectedFields,
        ]
      : [
          {
            name: 'Document type',
            value: UNCLASSIFIED_DOCUMENT,
            confidence: 1,
            source: TYPE_FROM_OPERATOR_SPLIT,
            needsReview: true,
            evidencePageNumber: mergedPages[0].number,
            evidenceLineIndexes: [],
          },
          ...mergedProtectedFields,
        ],
    lineItems: [...previous.lineItems, ...current.lineItems].map((row, index) => ({
      ...row,
      rowNumber: index + 1,
    })),
    needsReview: true,
  };
  const knownMergedSchema = sameType
    ? schemas.find(
        (schema) =>
          (previous.documentTypeId !== null &&
            previous.documentTypeId !== '' &&
            schema.id === previous.documentTypeId) ||
          schema.name.localeCompare(previous.documentType, undefined, { sensitivity: 'accent' }) ===
            0,
      )
    : undefined;
  return {
    kind: CORRECTION.boundaryMerge,
    mutation: {
      position: documentIndex - 1,
      removedDocumentIds: [previous.id, current.id],
      documents: [merged],
    },
    next: {
      ...working,
      documents: [
        ...working.documents.slice(0, documentIndex - 1),
        merged,
        ...working.documents.slice(documentIndex + 1),
      ],
    },
    targetId: current.id,
    metadata: { removedBoundaryBeforeBatchPage: current.pages[0].number },
    affectedDocumentIds: [merged.id],
    expected: [previous, current],
    superseded: supersededValues.map((item) => ({ documentId: merged.id, ...item })),
    recheckDocumentTypes: knownMergedSchema ? [knownMergedSchema] : undefined,
    landOn: current.pages[0].number,
  };
};

/** The correction a split or merge is saved with (save-boundary-mutation): every document's pages before and after. */
export const boundaryCorrection = <W extends BoundaryWorking, S>(
  working: BoundaryWorking,
  change: BoundaryChangeOf<W, S>,
  attribution?: ReviewAttribution,
): ReviewCorrection =>
  attributed(
    {
      kind: change.kind,
      targetId: change.targetId,
      beforeJson: JSON.stringify({
        documents: working.documents.map((document) => ({
          id: document.id,
          pages: document.pages.map((page) => page.number),
        })),
      }),
      afterJson: JSON.stringify({
        documents: change.next.documents.map((document) => ({
          id: document.id,
          pages: document.pages.map((page) => page.number),
        })),
      }),
      metadataJson: JSON.stringify(change.metadata),
    },
    attribution,
  );

/**
 * A correction the merge could not keep is still a change to a value an operator
 * made, so it goes into the same ledger every other value change goes into. Its own
 * kind, though: the engine projects field-value-correction and field-value-verified
 * into teaching examples, and the value that lost a merge is the last thing that
 * should be taught.
 */
export const supersededCorrection = (
  survivorId: string,
  entry: {
    fieldName: string;
    kept: FieldResult;
    discarded: FieldResult;
    discardedFromDocumentId: string;
  },
  removedDocumentIds: readonly string[],
  attribution?: ReviewAttribution,
): ReviewCorrection =>
  attributed(
    {
      kind: 'field-value-superseded',
      targetId: `${survivorId}:${entry.fieldName}`,
      beforeJson: JSON.stringify({
        documentId: entry.discardedFromDocumentId,
        fieldName: entry.fieldName,
        field: entry.discarded,
      }),
      afterJson: JSON.stringify({
        documentId: survivorId,
        fieldName: entry.fieldName,
        field: entry.kept,
      }),
      metadataJson: JSON.stringify({
        reason: CORRECTION.boundaryMerge,
        removedDocumentIds: removedDocumentIds,
      }),
    },
    attribution,
  );

/**
 * A file kept whole that the automatic detector thinks holds several documents (ADR-0091): how many,
 * and the batch pages they would start on. Mirrors the engine's SourceSeparation.SplitSuggestion: null
 * when there is none, or once a person confirmed where the document starts. `keptByLesson`: the job's
 * own confirmations kept it whole, so it is offered quietly rather than held.
 */
export const sourceSplitSuggestion = (
  document: Pick<DocumentResult, 'pages'>,
): { documents: number; startPages: number[]; keptByLesson: boolean } | null => {
  if (document.pages.length < 2) return null;
  const reasons = document.pages[0].boundaryReasons ?? [];
  if (reasons.some((reason) => /you confirmed/i.test(reason))) return null;
  const startPages = document.pages
    .slice(1)
    .filter((page) => page.detectedStart === true)
    .map((page) => page.number);
  if (startPages.length === 0) return null;
  return {
    documents: startPages.length + 1,
    startPages,
    keptByLesson: reasons.some((reason) => reason.startsWith('Kept whole:')),
  };
};

/**
 * "Keep as one" on a split suggestion: the boundary confirmation the whole-document confirm already
 * sends (boundary-verified with every page), so each page inside becomes a confirmed continuation the
 * separation learner reads, and the opening says a person confirmed it, which clears the suggestion.
 */
/**
 * Keep separate, answered at the seam with the previous document's last page beside this one's first:
 * this document starts here. Only that boundary is vouched for; the pages inside were not looked at, so
 * they are not claimed as continuations (a whole-document confirmation claims those).
 */
export const keepSeparateAtSeam = (
  current: DocumentResult,
  attribution?: ReviewAttribution,
): { document: DocumentResult; correction: ReviewCorrection } => {
  const opening = current.pages[0];
  const verifiedDocumentBatchPages = opening ? [opening.number] : [];
  return {
    document: {
      ...current,
      pages: current.pages.map((page, index) =>
        index !== 0
          ? page
          : {
              ...page,
              boundaryConfidence: 1,
              boundaryReasons: ['You confirmed this document starts here'],
            },
      ),
    },
    correction: attributed(
      {
        kind: CORRECTION.boundaryVerified,
        targetId: `${current.id}:boundaries`,
        beforeJson: JSON.stringify({
          documentId: current.id,
          batchPages: verifiedDocumentBatchPages,
          boundaryConfidence: opening?.boundaryConfidence,
          boundaryReasons: opening?.boundaryReasons ?? [],
        }),
        afterJson: JSON.stringify({
          documentId: current.id,
          batchPages: verifiedDocumentBatchPages,
        }),
        metadataJson: JSON.stringify({
          verifiedStartBatchPage: opening?.number,
          verifiedDocumentBatchPages,
          keptSeparateAtSeam: true,
        }),
      },
      attribution,
    ),
  };
};
export const keepFileWhole = (
  current: DocumentResult,
  attribution?: ReviewAttribution,
): { document: DocumentResult; correction: ReviewCorrection } => {
  const opening = current.pages[0];
  const verifiedDocumentBatchPages = current.pages.map((page) => page.number);
  return {
    document: {
      ...current,
      pages: current.pages.map((page, index) =>
        index !== 0
          ? page
          : {
              ...page,
              boundaryConfidence: 1,
              boundaryReasons: ['You confirmed this document starts here'],
            },
      ),
    },
    correction: attributed(
      {
        kind: CORRECTION.boundaryVerified,
        targetId: `${current.id}:boundaries`,
        beforeJson: JSON.stringify({
          documentId: current.id,
          batchPages: verifiedDocumentBatchPages,
          boundaryConfidence: opening?.boundaryConfidence,
          boundaryReasons: opening?.boundaryReasons ?? [],
        }),
        afterJson: JSON.stringify({
          documentId: current.id,
          batchPages: verifiedDocumentBatchPages,
        }),
        metadataJson: JSON.stringify({
          verifiedStartBatchPage: opening?.number,
          verifiedDocumentBatchPages,
          keptFileWhole: true,
        }),
      },
      attribution,
    ),
  };
};
